import * as React from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import type { RootState, Dispatch } from '../../app/store';
import type { StoreT } from '../../models/store';
import Close from '../../icons/Close';
import styles from './Header.module.css';
import Modal from '../Modal'
import { useInactiveContext } from '../../contexts/InactiveContext'
import { INACTIVITY_TIMEOUT, INACTIVITY_COUNTDOWN_SECONDS } from '../../models/constants';

const lightModeStartPage = {
  täby: '0594bfa2-9dc1-423d-53a0-08d6d842082e',
  roslagstull: '8ea8885a-0930-4a4b-0bab-08db0f57590e',
}

interface HeaderT {
  fetchStore: (payload: string) => void;
  store?: StoreT | null;
  withCancelButton?: boolean;
  children?: React.ReactNode;
  resetCart: () => void;
}

function Header ({
  resetCart,
  fetchStore,
  store,
  withCancelButton,
  children,
}: HeaderT) {
  const { storeId } = useParams()
  const { name, logo, alternateLogo } = store || {};
  const navigate = useNavigate();
  const location = useLocation()
  const { lastClickTime } = useInactiveContext()
  const [inactivityModal, setInactivityModal] = React.useState<boolean>(false)
  const [timer, setTimer] = React.useState<null | number>(null)


  const logoUrl = React.useMemo(() => {
    const selectedLogo = alternateLogo || logo || ''
    return Object.values(lightModeStartPage).includes(storeId as string) ?
      selectedLogo.replace('upload/', 'upload/e_bgremoval/') : selectedLogo

  }, [alternateLogo, logo])

  const getStore = React.useCallback(() => {
    storeId && fetchStore(storeId);
  }, [fetchStore, storeId]);

  React.useEffect(() => {
    if (!store || !store.hasOwnProperty('name')) {
      getStore();
    }
  }, [getStore, store]);

  const onCancelOrder = React.useCallback(() => {
    resetCart()
    navigate(`/butik/${storeId}`)
  }, [resetCart, navigate, storeId])

  React.useEffect(() => {
    if (!inactivityModal || timer === null) return

    if (timer === 0) {
      setInactivityModal(false)
      onCancelOrder()
      return
    }

    const interval = setInterval(() => {
      setTimer(prev => prev !== null && prev > 0 ? prev - 1 : null)
    }, 1000)

    return () => clearInterval(interval)
  }, [timer, inactivityModal, onCancelOrder])

  React.useEffect(() => {
    if (location.pathname === `/butik/${storeId}` || location.pathname.includes('/tack') || inactivityModal) return

    const checkInactivity = () => {
      const currentTime = Date.now()
      if (currentTime - lastClickTime >= INACTIVITY_TIMEOUT) {
        setInactivityModal(true)
        setTimer(INACTIVITY_COUNTDOWN_SECONDS)
      }
    }

    const timeout = setTimeout(checkInactivity, INACTIVITY_TIMEOUT)
    return () => clearTimeout(timeout)
  }, [lastClickTime, location.pathname, storeId, inactivityModal])

  return (
    <header className={styles.header}>
      <div className={styles.content}>
        <a className={styles.link} href={`/butik/${storeId}`}>
          {logoUrl && (
            <img className={styles.storeLogo} src={logoUrl} alt={`${name} logo`} />
          )}
          <p className={styles.storeName}>{name}</p>
          {children}
        </a>
      </div>
      {withCancelButton === true && (
        <button
          type="button"
          onClick={onCancelOrder}
          className={styles.baseButton}
        >
          <span className={styles.cancelText}>Avbryt order</span>
          <Close color="#cf2e05" className={styles.cancelIcon} />
        </button>
      )}
        <Modal
        maxWidth='770px'
        active={inactivityModal}
        zIndex={200}
        onConfirm={() => setInactivityModal(false)}
        onCancel={() => setInactivityModal(false)}
      >
        <div className={styles.modalContent}>
          <h2 className={styles.modalHeading}>Är du kvar?</h2>
          <div className={styles.modalButtonGroup}>
            <button className={styles.modalProceedButton} onClick={() => setInactivityModal(false)}>Ja, fortsätt</button>
            <button className={styles.modalCancelButton} onClick={() => onCancelOrder()}>Nej, avbryt order</button>
          </div>
        </div>
      </Modal>
    </header>
  );
}

function mapState (state: RootState) {
  return {
    store: state.store.current,
  };
}
function mapDispatch (dispatch: Dispatch) {
  return {
    fetchStore: dispatch.store.fetchStore,
    resetCart: () => dispatch({ type: 'RESET_CART' }),
  };
}

export default connect(mapState, mapDispatch)(Header);
